"use client";

import { COLORS } from '@/assets/static/colors';
import CheckmarkAppFilled from '@/assets/vectors/CheckmarkAppFilled';
import Facebook from "@/assets/vectors/Facebook";
import Link from "@/assets/vectors/Link";
import LinkedIn from "@/assets/vectors/LinkedIn";
import X from "@/assets/vectors/X";
import Button from "@/components/button/Button";
import CaseStudy from "@/types/CaseStudy";
import dictionary from "@/utils/dictionary";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import styles from "./case_study_social_share.module.scss";

interface CaseStudySocialShareProps {
    caseStudy: CaseStudy;
}

export default function CaseStudySocialShare({ caseStudy }: CaseStudySocialShareProps) {
    const pathname = usePathname();
    const [copied, setCopied] = useState(false);
    const [shareUrl, setShareUrl] = useState("");

    useEffect(() => {
        setShareUrl(`${window.location.origin}${pathname}`);
    }, [pathname]);

    const shareTitle = caseStudy.hero_title;

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy link:", err);
        }
    };

    const shareLinks = [
        {
            name: "LinkedIn",
            icon: LinkedIn,
            url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
            color: COLORS.cyan,
        },
        {
            name: "Facebook",
            icon: Facebook,
            url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
            color: COLORS.blue,
        },
        {
            name: "Twitter",
            icon: X,
            url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`,
            color: COLORS.white,
        },
    ];

    return (
        <section className={styles.container} id="section_share">
            <div className={styles.content}>
                <h3
                    className={styles.title}
                    data-scroll-animation="come-from-bottom"
                >{dictionary.global.share_case_study}</h3>

                <div className={styles.buttons}>
                    {shareLinks.map((social, index) => (
                        <Button
                            key={social.name}
                            href={social.url}
                            label={social.name}
                            hrefTarget="_blank"
                            startIcon={social.icon}
                            color={social.color}
                            style='bezeled'
                            rel="noopener noreferrer"
                            className={styles.share_button}
                            aria-label={`Share on ${social.name}`}
                            data-scroll-animation="come-from-bottom"
                            data-scroll-animation-delay={index * 75}
                        />
                    ))}

                    <Button
                        onClick={handleCopyLink}
                        startIcon={copied ? CheckmarkAppFilled : Link}
                        status={copied ? "success" : "enabled"}
                        label={dictionary.global.copy_link}
                        style='bezeled'
                        color={COLORS.orange}
                        successLabel={dictionary.global.link_copied}
                        aria-label="Copy link"
                        data-scroll-animation="come-from-bottom"
                        data-scroll-animation-delay="300"
                    />
                </div>
            </div>
        </section>
    );
}
